/**
 * Supply Audit Trail Panel
 * Displays immutable log of all preference card changes with dictation evidence.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Shield,
  FileText,
  ArrowRight,
  Minus,
  Hash,
  Clock,
  Mic,
  Lock,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface AuditEntry {
  id: string;
  timestamp: string;
  action: string;
  changeType: 'item_removed' | 'item_replaced' | 'quantity_adjusted' | 'card_created' | 'fhir_synced';
  itemBefore?: { name: string; size?: string; qty: number };
  itemAfter?: { name: string; size?: string; qty: number };
  dictationEvidence?: string;
  caseCount?: number;
  confidence?: number;
  triggeredBy: string;
  procedureName: string;
  cptCode: string;
}

const DEMO_AUDIT: AuditEntry[] = [
  {
    id: 'aud-1',
    timestamp: '2026-02-10T16:42:00Z',
    action: 'Preference card item replaced',
    changeType: 'item_replaced',
    itemBefore: { name: '4-0 Vicryl', size: '4-0', qty: 4 },
    itemAfter: { name: '5-0 Monocryl', size: '5-0', qty: 3 },
    dictationEvidence: '"…closure with 5-0 Monocryl subcuticular running suture…"',
    caseCount: 8,
    confidence: 100,
    triggeredBy: 'Dr. John J. Larkin (Surgeon Sync)',
    procedureName: 'Carpal Tunnel Release',
    cptCode: '64721',
  },
  {
    id: 'aud-2',
    timestamp: '2026-02-10T16:42:01Z',
    action: 'FHIR SupplyRequest pushed to EHR',
    changeType: 'fhir_synced',
    triggeredBy: 'LINK OR-Sync Engine',
    procedureName: 'Carpal Tunnel Release',
    cptCode: '64721',
  },
  {
    id: 'aud-3',
    timestamp: '2026-02-09T14:15:00Z',
    action: 'Preference card item removed',
    changeType: 'item_removed',
    itemBefore: { name: 'Autograft Bone Chips', qty: 1 },
    dictationEvidence: 'No bone graft referenced in any of last 12 operative notes.',
    caseCount: 12,
    confidence: 92,
    triggeredBy: 'Dr. John J. Larkin (Surgeon Sync)',
    procedureName: 'Total Knee Arthroplasty',
    cptCode: '27447',
  },
  {
    id: 'aud-4',
    timestamp: '2026-02-09T14:15:01Z',
    action: 'FHIR SupplyRequest pushed to EHR',
    changeType: 'fhir_synced',
    triggeredBy: 'LINK OR-Sync Engine',
    procedureName: 'Total Knee Arthroplasty',
    cptCode: '27447',
  },
  {
    id: 'aud-5',
    timestamp: '2026-02-06T11:30:00Z',
    action: 'Preference card quantity adjusted',
    changeType: 'quantity_adjusted',
    itemBefore: { name: 'Closed Suction Drain', qty: 2 },
    itemAfter: { name: 'Closed Suction Drain', qty: 1 },
    dictationEvidence: '"…single Hemovac drain placed in the lateral gutter…"',
    caseCount: 6,
    confidence: 83,
    triggeredBy: 'Dr. John J. Larkin (Surgeon Sync)',
    procedureName: 'Total Hip Arthroplasty',
    cptCode: '27130',
  },
];

function ChangeIcon({ type }: { type: AuditEntry['changeType'] }) {
  if (type === 'item_removed') return <Minus className="w-3.5 h-3.5 text-destructive" />;
  if (type === 'item_replaced') return <ArrowRight className="w-3.5 h-3.5 text-primary" />;
  if (type === 'quantity_adjusted') return <Hash className="w-3.5 h-3.5 text-warning" />;
  if (type === 'fhir_synced') return <Shield className="w-3.5 h-3.5 text-success" />;
  return <FileText className="w-3.5 h-3.5 text-muted-foreground" />;
}

function changeBadgeColor(type: AuditEntry['changeType']): string {
  const map: Record<string, string> = {
    item_removed: 'border-destructive/30 text-destructive',
    item_replaced: 'border-primary/30 text-primary',
    quantity_adjusted: 'border-warning/30 text-warning',
    fhir_synced: 'border-success/30 text-success',
    card_created: 'border-border text-muted-foreground',
  };
  return map[type] || '';
}

function changeLabel(type: AuditEntry['changeType']): string {
  const map: Record<string, string> = {
    item_removed: 'REMOVED',
    item_replaced: 'REPLACED',
    quantity_adjusted: 'QTY ADJ',
    fhir_synced: 'EHR SYNC',
    card_created: 'CREATED',
  };
  return map[type] || type;
}

export function SupplyAuditTrailPanel() {
  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-primary" />
          <div>
            <CardTitle className="text-base">Immutable Supply Audit Trail</CardTitle>
            <CardDescription className="text-xs">
              Every preference card change is logged with the dictation evidence that triggered it. Records are append-only and non-deletable.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <Separator />
      <CardContent className="pt-3 space-y-0">
        {DEMO_AUDIT.map((entry, idx) => (
          <div key={entry.id} className="relative pl-6 pb-4 last:pb-0">
            {/* Timeline connector */}
            {idx < DEMO_AUDIT.length - 1 && (
              <div className="absolute left-[11px] top-5 bottom-0 w-px bg-border" />
            )}
            <div className="absolute left-0 top-1 w-[22px] h-[22px] rounded-full border border-border bg-card flex items-center justify-center">
              <ChangeIcon type={entry.changeType} />
            </div>

            <div className="space-y-1.5">
              {/* Header */}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium">{entry.action}</p>
                  <p className="text-xs text-muted-foreground">
                    {entry.procedureName} · CPT {entry.cptCode}
                  </p>
                </div>
                <Badge variant="outline" className={cn('text-[10px] shrink-0', changeBadgeColor(entry.changeType))}>
                  {changeLabel(entry.changeType)}
                </Badge>
              </div>

              {/* Change detail */}
              {(entry.itemBefore || entry.itemAfter) && (
                <div className="flex items-center gap-2 text-xs">
                  {entry.itemBefore && (
                    <span className="line-through text-muted-foreground">
                      {entry.itemBefore.size ? `${entry.itemBefore.size} ` : ''}{entry.itemBefore.name} ×{entry.itemBefore.qty}
                    </span>
                  )}
                  {entry.itemAfter && (
                    <>
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <span className="font-medium">
                        {entry.itemAfter.size ? `${entry.itemAfter.size} ` : ''}{entry.itemAfter.name} ×{entry.itemAfter.qty}
                      </span>
                    </>
                  )}
                </div>
              )}

              {/* Dictation evidence */}
              {entry.dictationEvidence && (
                <div className="bg-muted/50 rounded-lg px-3 py-2 flex items-start gap-2">
                  <Mic className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                  <p className="text-xs italic text-muted-foreground leading-relaxed">
                    {entry.dictationEvidence}
                  </p>
                </div>
              )}

              {/* Meta */}
              <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(entry.timestamp).toLocaleString()}
                </span>
                {entry.caseCount && <span>{entry.caseCount} cases</span>}
                {entry.confidence && <span>{entry.confidence}% confidence</span>}
                <span className="ml-auto">{entry.triggeredBy}</span>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
